import axios from 'axios';
import { t } from 'i18next';
import qs from 'qs';
import { SubmissionError } from 'redux-form';
import { endpoints } from '../../utils/constants';
import {
  getAddress, getZip, getCity, getState, getCountry, getAddressOnly, serviceLevelCodeSelection,
} from '../../utils/func';
import {
  DeliveryTypes, PaymentMethods, SamedayServiceType, BillToMethods, SamedayShipmentType,
} from '../../utils/enums';
import { parseAttachedDocuments } from '../../utils/uploadedFiles';
import { accessorialsLists, measurementLists } from '../../utils/orders';

export const fetchAllQuotesService = (url) => axios.get(axios.get(url));

export const apiGetQuotesAll = (
  accountNumbers,
  statusCodes,
  serviceLevels,
  serviceTypes,
  fromDate,
  toDate,
  pageSize,
  pageNumber,
  orderBy,
  sortDirection,
  keySearch,
) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageQuotes.list}`, {
    params: {
      search: keySearch,
      accountNumbers,
      statusCodes,
      serviceLevels,
      serviceTypes,
      fromDate,
      toDate,
      pageSize,
      pageNumber,
      orderBy,
      sortDirection,
    },
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

export const apiGetQuoteReports = (accountNumbers, columns, fromDate, toDate) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageQuotes.export}`, {
    params: {
      accountNumbers,
      columns,
      fromDate,
      toDate,
    },
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

const formValuesToAPI = (formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated) => {
  const {
    deliveryType,
    division,
    paymentMethod,
    shipperContactName,
    shipperCompanyName,
    shipperAddress,
    shipperAddressAdditional,
    shipperEmail,
    shipperPhoneNumber,
    shipperPhoneNumberExt,
    shipperMobileSMSNotification,
    shipperResidentialAddress,
    shipperProvinceState,
    shipperPostalZipCode,
    consigneeAddressAdditional,
    consigneeEmail,
    consigneePhoneNumber,
    consigneeAddress,
    consigneeContactName,
    consigneeCompanyName,
    consigneePhoneNumberExt,
    consigneeMobileSMSNotification,
    consigneeResidentialAddress,
    consigneeProvinceState,
    consigneePostalZipCode,
    measurementType,
    measurementList,
    accessorials,
    quoteDest,
    pickupDate,
    emergencyPhone,
    erapReference,
    erapPhone,
    referenceNumber,
    poNumber,
    specialInstructions,
    readyTime,
    closingTime,
    shipmentType,
    accessByTrailer,
    droppingFreightOff,
    regularScheduledPickup,
    attachedDocuments,
    billingAddressAdditional,
    billingEmail,
    billingPhoneNumber,
    billingAddress,
    billingContactName,
    billingCompanyName,
    billingPhoneNumberExt,
    gstNumber,
    billToMethod,
    billTo,
    thirdPartyAccountNumber,
    selectedUnits,
    sendConsigneeInfo,
    isQuote,
  } = formData;

  const shipperAddress1 = getAddressOnly(shipperAddress);
  const { shipperCity } = formData;
  const shipperState = shipperProvinceState;
  const shipperZip = shipperPostalZipCode;
  const { shipperCountry } = formData;

  const consigneeAddress1 = getAddressOnly(consigneeAddress);
  const { consigneeCity } = formData;
  const consigneeState = consigneeProvinceState;
  const consigneeZip = consigneePostalZipCode;
  const { consigneeCountry } = formData;

  const formValueToApi = {
    shipmentId: 0,
    accountNumber: billToMethod !== BillToMethods.enterBillTo ? billTo : thirdPartyAccountNumber,
    createByUserId: userId,
    createByUserEmail: userEmail,
    caller: isAuthenticated ? { name: userName, contact: userName, phone: shipperPhoneNumber } : { name: 'WEBSITE GUEST', contact: 'WEBSITE GUEST' },
    serviceType: (deliveryType && deliveryType.name === DeliveryTypes.Residential) ? SamedayServiceType.Residential : SamedayServiceType.Commercial,
    paymentType: paymentMethod.split(' ').join(''),
    language: 'EN',
    division: division || 'Sameday',
    from: {
      companyName: shipperCompanyName,
      contactName: shipperContactName,
      address1: (shipperAddress1 && shipperAddress1 !== 'null') ? shipperAddress1 : null,
      address2: shipperAddressAdditional || '',
      city: (shipperCity && shipperCity !== 'null') ? shipperCity : null,
      provinceCode: (shipperState && shipperState !== 'null') ? shipperState : null,
      countryCode: shipperCountry,
      postalCode: shipperZip,
      contactPhoneNumber: shipperPhoneNumber,
      contactPhoneNumberExt: shipperPhoneNumberExt || '',
      cellNumber: shipperMobileSMSNotification || '',
      contactEmail: shipperEmail,
      isResidential: shipperResidentialAddress,
    },
    to: {
      companyName: consigneeCompanyName,
      contactName: consigneeContactName,
      address1: (consigneeAddress1 && consigneeAddress1 !== 'null') ? consigneeAddress1 : null,
      address2: consigneeAddressAdditional || '',
      city: (consigneeCity && consigneeCity !== 'null') ? consigneeCity : null,
      provinceCode: (consigneeState && consigneeState !== 'null') ? consigneeState : null,
      countryCode: consigneeCountry,
      postalCode: consigneeZip,
      contactPhoneNumber: consigneePhoneNumber,
      contactPhoneNumberExt: consigneePhoneNumberExt || '',
      cellNumber: consigneeMobileSMSNotification || '',
      contactEmail: consigneeEmail,
      isResidential: consigneeResidentialAddress,
    },
    accessorials: accessorialsLists(accessorials),
    shipmentItems: measurementLists(measurementList, measurementType.value, isQuote),
    shipmentType: shipmentType || SamedayShipmentType.Regular,
    quoteNumber: null,
    confirmationEmail: quoteDest,
    measurementType: (selectedUnits.indexOf(t('BtnImperial')) > -1) ? 1 : 0,
    specialInstructions: specialInstructions || '',
    droppingFreightOff: droppingFreightOff ? droppingFreightOff.value : false,
    hasRegularPickups: regularScheduledPickup ? regularScheduledPickup.value : false,
    isAllowAccessByTrailer: accessByTrailer.value,
    pickupDetails: regularScheduledPickup.value ? undefined : {
      pickupDate,
      readyTime: (readyTime)
        ? `${readyTime.hours.length < 2 ? `0${readyTime.hours}`
          : readyTime.hours}:${readyTime.minutes.length < 2 ? `0${readyTime.minutes}` : readyTime.minutes} ${readyTime.amOrPm}` : null,
      closingTime: (closingTime)
        ? `${closingTime.hours.length < 2 ? `0${closingTime.hours}`
          : closingTime.hours}:${closingTime.minutes.length < 2 ? `0${closingTime.minutes}` : closingTime.minutes} ${closingTime.amOrPm}` : null,
    },
    referenceNumber: referenceNumber || '',
    serviceLevelCode: serviceLevelCode || '',
    poNumber: poNumber || '',
    emergencyPhoneNumber: emergencyPhone || '',
    erapReferenceNumber: erapReference || '',
    erapPhoneNumber: erapPhone || '',
    sendEmailToConsignee: sendConsigneeInfo,
    thirdPartyDetails: paymentMethod === PaymentMethods.ThirdParty ? {
      companyName: billingCompanyName,
      contactName: billingContactName,
      address1: getAddress(billingAddress),
      address2: billingAddressAdditional,
      city: getCity(billingAddress),
      provinceCode: getState(billingAddress),
      countryCode: getCountry(billingAddress),
      postalCode: getZip(billingAddress),
      contactPhoneNumber: billingPhoneNumber,
      contactPhoneNumberExt: billingPhoneNumberExt,
      contactEmail: billingEmail,
    } : {
      companyName: '',
      contactName: '',
      address1: '',
      address2: '',
      city: '',
      provinceCode: '',
      countryCode: '',
      postalCode: '',
      contactPhoneNumber: '',
      contactPhoneNumberExt: '',
      contactEmail: '',
      cellNumber: '',
    },
    gstNumber: gstNumber || '',
    thirdPartyAccountNumber: thirdPartyAccountNumber || '',
    estimateDeliveryDate: null,
    brokerName: '',
    brokerEmail: '',
    brokerPhoneNumber: '',
  };

  parseAttachedDocuments(attachedDocuments, formValueToApi);

  return formValueToApi;
};

export const apiCreateQuote = (formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated, updateState, callback) => {
  const serviceLevels = serviceLevelCodeSelection(formData);
  const formValuesAPI = formValuesToAPI(formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated);
  const defaultServiceLevelCode = Array.isArray(serviceLevels) ? serviceLevels[0].code : serviceLevels.code;
  formValuesAPI.serviceLevelCode = serviceLevelCode || defaultServiceLevelCode || formData.serviceLevelCode;
  formValuesAPI.caller = isAuthenticated
    ? {
      name: userName, contact: userName, phone: formData.shipperPhoneNumber, clientId: formValuesAPI.accountNumber,
    }
    : {
      name: 'WEBSITE GUEST', contact: 'WEBSITE GUEST', phone: '999-999-9999', clientId: formValuesAPI.accountNumber,
    };
  return axios.post(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageQuotes.create}`, formValuesAPI).then((res) => {
    if (res.status === 200 || res.status === 201) {
      updateState('data', {
        probillNumber: res.data.probillNumber,
        orderId: res.data.orderId,
        quoteNumber: res.data.shipmentNumber,
        totalCharges: res.data.totalCharges,
        charges: res.data.charges,
        estimatedDeliveryDate: res.data.estimatedDeliveryDate,
        bolPdf: res.data.bolPdf,
        labelsPdf: res.data.labelsPdf,
        labelsZpl: res.data.labelsZpl,
        quotePdf: res.data.quotePdf,
        totalAmount: res.data.totalAmount,
        shipmentNumber: res.data.shipmentNumber,
        serviceLevelCode: res.data.serviceLevelCode,
        serviceLevelDescription: res.data.serviceLevelDescription,
        tax: res.data.tax,
        underReview: res.data.underReview,
      }, callback);
      window.scrollTo(0, 0);
      return res.data;
    }

    return 0;
  }).catch((error) => {
    if (error && error.response && error.response.data) {
      const errorMessage = error.response.data.split('\n')[0];
      const sepIndex = errorMessage.indexOf(' ');
      if (sepIndex > 0 && errorMessage.length > sepIndex) {
        throw new SubmissionError({ message: JSON.parse(errorMessage.substring(sepIndex + 1)) });
      }
    }

    throw new SubmissionError({});
  });
};

export const getQuotesByDivision = ({ division, numberOfDaysFromToday, accountNumbers }) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}`, {
    params: {
      numberOfDaysFromToday,
    },
    headers: {
      accountNumbers,
    },
  });

export const postQuoteServiceLevels = (data) =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.serviceLevel}`, data);

export const postQuote = (data) =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}`, data);

export const getQuoteByDivisionAndQuoteNumber = (division, quoteNumber, USQuote) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}/${quoteNumber}${USQuote ? '/us' : ''}`);

export const getQuoteDocument = ({
  division, quoteNumber, language, USQuote,
}) =>
  axios.get(
    `${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}/${quoteNumber}${USQuote ? '/us' : ''}/document`,
    {
      params: {
        language: language || '',
      },
    });

export const postQuoteToUSRates = (data) =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/us-rate`, data);

export const postQuoteToUS = () =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/us`);

export const apiGetQuotes = (division, accountNumbers, fromDate, toDate) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}`, {
    params: {
      fromDate,
      toDate,
    },
    headers: {
      accountNumbers,
    },
  });

export const apiGetSameDayQuotes = (division, accountNumbers, fromDate, toDate) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}`, {
    params: {
      fromDate,
      toDate,
    },
    headers: {
      accountNumbers,
    },
  });

export const apiGetFilteredQuotes = (division, accountNumbers, fromDate, toDate, quoteType) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.quotes}/${division}`, {
    params: {
      fromDate,
      toDate,
      quoteType,
    },
    headers: {
      accountNumbers,
    },
  });
